import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from "sonner";
import { Copy, Code, Settings } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface EmbedSectionProps {
  positId: string;
  isPremium?: boolean;
  isPublic?: boolean;
}

export function EmbedSection({ positId, isPremium = false, isPublic = false }: EmbedSectionProps) {
  const [isEmbedEnabled, setIsEmbedEnabled] = useState(false);
  const [embedDomains, setEmbedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [embedType, setEmbedType] = useState<'widget' | 'minimal' | 'ultra-minimal' | 'iframe'>('widget');
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  // Load existing embed configuration
  useEffect(() => {
    const loadEmbedConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('posits')
          .select('embed_enabled, embed_domains, embed_code')
          .eq('id', positId)
          .single();

        if (error) throw error;

        if (data) {
          setIsEmbedEnabled(data.embed_enabled || false);
          setEmbedDomains(data.embed_domains || []);
          setEmbedCode(data.embed_code || '');
        }
      } catch (error) {
      }
    };

    loadEmbedConfig();
  }, [positId]);

  // Generate embed code when enabled
  useEffect(() => {
    if (isEmbedEnabled) {
      const baseUrl = window.location.origin;
      let code = '';
      
      switch (embedType) {
        case 'widget':
          code = `<div data-posit-embed="${positId}" data-posit-theme="auto"></div>
<script src="${baseUrl}/posit-embed.js"></script>`;
          break;
        case 'minimal':
          code = `<iframe 
  src="${baseUrl}/embedded-only/posit/${positId}?theme=auto"
  width="100%" 
  height="400" 
  frameborder="0"
  style="border-radius: 8px; border: 1px solid #e5e7eb;">
</iframe>`;
          break;
        case 'ultra-minimal':
          code = `<iframe 
  src="${baseUrl}/minimal/posit/${positId}?theme=auto"
  width="100%" 
  height="350" 
  frameborder="0"
  style="border: none;">
</iframe>`;
          break;
        case 'iframe':
          code = `<iframe 
  src="${baseUrl}/embed/posit/${positId}?theme=auto&branding=true"
  width="100%" 
  height="600" 
  frameborder="0"
  style="border-radius: 12px; border: 1px solid #e5e7eb;">
</iframe>`;
          break;
      }
      
      setEmbedCode(code);
    }
  }, [isEmbedEnabled, positId, embedType]);

  const handleToggleEmbed = async (enabled: boolean) => {
    if (!isPublic) {
      toast.error("Only public Posits can be embedded");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('posits')
        .update({ 
          embed_enabled: enabled,
          embed_code: enabled ? embedCode : null
        })
        .eq('id', positId);

      if (error) throw error;

      setIsEmbedEnabled(enabled);
      
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        status: 'success',
        share_type: 'embed',
        embed_enabled: enabled,
        is_premium: isPremium
      });

      toast.success(enabled ? "Embedding enabled" : "Embedding disabled");
    } catch (error) {
      toast.error("Failed to update embedding settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;

    // Simple domain validation
    const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      toast.error("Please enter a valid domain (e.g., example.com)");
      return;
    }

    const updatedDomains = [...embedDomains, newDomain.trim()];
    
    try {
      const { error } = await supabase
        .from('posits')
        .update({ embed_domains: updatedDomains })
        .eq('id', positId);

      if (error) throw error;

      setEmbedDomains(updatedDomains);
      setNewDomain('');
      toast.success("Domain added successfully");
    } catch (error) {
      toast.error("Failed to add domain");
    }
  };

  const handleRemoveDomain = async (domainToRemove: string) => {
    const updatedDomains = embedDomains.filter(domain => domain !== domainToRemove);
    
    try {
      const { error } = await supabase
        .from('posits')
        .update({ embed_domains: updatedDomains })
        .eq('id', positId);

      if (error) throw error;

      setEmbedDomains(updatedDomains);
      toast.success("Domain removed successfully");
    } catch (error) {
      toast.error("Failed to remove domain");
    }
  };

  const handleAllowAll = async () => {
    try {
      const { error } = await supabase
        .from('posits')
        .update({ embed_domains: [] })
        .eq('id', positId);

      if (error) throw error;

      setEmbedDomains([]);
      toast.success("Embedding allowed on all domains");
    } catch (error) {
      toast.error("Failed to update domain settings");
    }
  };

  const handleCopyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopiedEmbed(true);
      
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        status: 'success',
        share_type: 'embed_code',
        is_premium: isPremium
      });
      
      toast.success("Embed code copied to clipboard");
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (err) {
      toast.error("Failed to copy embed code");
    }
  };

  if (!isPublic) {
    return (
      <div className="p-4 border rounded-lg bg-muted/50">
        <div className="flex items-center gap-2 mb-2">
          <Code className="h-4 w-4" />
          <Label className="text-sm font-medium">Embed on Websites</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Only public Posits can be embedded. Change your Posit's privacy setting to "Public" to enable embedding.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="h-4 w-4" />
          <Label className="text-sm font-medium">Embed on Websites</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={isEmbedEnabled}
            onCheckedChange={handleToggleEmbed}
            disabled={isLoading}
          />
          {isEmbedEnabled && (
            <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Settings className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Embed Configuration</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Domain Restrictions</Label>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to allow embedding on any domain. Add specific domains to restrict where your Posit can be embedded.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="example.com"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddDomain();
                          }
                        }}
                      />
                      <Button onClick={handleAddDomain}>Add</Button>
                      <Button 
                        variant="outline" 
                        onClick={handleAllowAll}
                        disabled={embedDomains.length === 0}
                      >
                        Allow All
                      </Button>
                    </div>
                    {embedDomains.length > 0 && (
                      <div className="space-y-1">
                        {embedDomains.map((domain, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                            <span className="text-sm">{domain}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveDomain(domain)}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {isEmbedEnabled && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Embed Type</Label>
            <Select value={embedType} onValueChange={(value: 'widget' | 'minimal' | 'ultra-minimal' | 'iframe') => setEmbedType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="widget">JavaScript Widget (Recommended)</SelectItem>
                <SelectItem value="minimal">Minimal Chat Only</SelectItem>
                <SelectItem value="ultra-minimal">Ultra-Minimal (No Dependencies)</SelectItem>
                <SelectItem value="iframe">Full Embed with Header</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              {embedType === 'widget' && "Dynamic widget that adapts to your site's theme and loads efficiently."}
              {embedType === 'minimal' && "Clean chat interface only - no headers, footers, or extra UI elements."}
              {embedType === 'ultra-minimal' && "Absolutely minimal with zero dependencies - uses only inline styles, no navigation."}
              {embedType === 'iframe' && "Complete embed with header, branding, and expand options."}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Embed Code</Label>
            <div className="flex gap-2">
              <Textarea
                value={embedCode}
                readOnly
                className="text-xs font-mono"
                rows={embedType === 'widget' ? 3 : embedType === 'ultra-minimal' ? 6 : 7}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyEmbedCode}
                className="shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Copy and paste this code into any website to embed your Posit.
            </p>
          </div>
        </div>
      )}
    </div>
  );
} 